import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { createCheckoutSession } from "../../api/subscriptionClient";

/**
 * Renders the primary trial CTA, routed correctly for the viewer's state:
 *   - Not authenticated         → <Link to="/signup">{children}</Link>
 *   - Authenticated, active sub → <Link to="/portfolio">Go to dashboard</Link>
 *   - Authenticated, no sub     → button that triggers Stripe checkout directly
 *
 * While subscription status is still loading, the authenticated branch falls
 * back to /portfolio (SubscriptionRequired handles the unsubscribed case there)
 * so we never flash the wrong label for real subscribers.
 */
export default function StartTrialCta({
    plan = "monthly",
    className = "",
    dashboardLabel = "Go to dashboard",
    onCheckoutError,
    children,
}) {
    const { isAuthenticated } = useAuth();
    const { hasActiveSubscription, loading: subLoading } = useSubscription();
    const navigate = useNavigate();
    const [checkoutLoading, setCheckoutLoading] = useState(false);

    if (!isAuthenticated) {
        return (
            <Link to="/signup" className={className}>
                {children}
            </Link>
        );
    }

    // Authenticated + confirmed active subscription, OR still loading (fall back
    // to /portfolio; SubscriptionRequired will redirect to checkout if needed).
    if (subLoading || hasActiveSubscription) {
        return (
            <Link to="/portfolio" className={className}>
                {dashboardLabel}
            </Link>
        );
    }

    const handleClick = async () => {
        setCheckoutLoading(true);
        try {
            const { checkoutUrl } = await createCheckoutSession({ plan, withTrial: true });
            window.location.href = checkoutUrl;
        } catch (err) {
            setCheckoutLoading(false);
            if (onCheckoutError) {
                onCheckoutError(err);
            } else {
                // Fall back to the subscribe page, which surfaces its own error UI.
                navigate("/subscribe");
            }
        }
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            disabled={checkoutLoading}
            className={className}
        >
            {checkoutLoading ? "Redirecting…" : children}
        </button>
    );
}
